import { useCallback, useEffect, useState } from 'react';
import { MapPin, ScrollText, ShieldCheck, ShieldAlert } from 'lucide-react';
import { usePlant } from '../../state/PlantContext';
import { adminApi, ApiError } from '../../lib/api';
import type { AuditLogEntry } from '../../lib/types';
import { Spinner, EmptyState, ErrorBanner, DisclosureBanner } from '../../components/Misc';
import { formatDateTime } from '../../lib/format';

export default function AuditLogPage() {
  const { currentPlant } = usePlant();
  const [entries, setEntries] = useState<AuditLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verifyResult, setVerifyResult] = useState<{ valid: boolean; entriesChecked: number; firstBrokenSeq: number | null; reason: string | null } | 'error' | null>(null);
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await adminApi.auditLog({ plantId: currentPlant?.id, limit: 200 });
      setEntries(res.entries);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the audit log.');
    }
  }, [currentPlant]);

  useEffect(() => {
    setEntries(null);
    void load();
  }, [load]);

  async function verify() {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await adminApi.verifyAuditChain();
      setVerifyResult(res);
    } catch {
      setVerifyResult('error');
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="stack gap-5">
      <div className="row" style={{ flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="h2-section">Audit log</h1>
          <p className="body-text text-secondary">Every safety-relevant action, permanently chained and tamper-evident.</p>
        </div>
        <div className="spacer" />
        <button className="btn btn-secondary" onClick={verify} disabled={verifying}>
          <ShieldCheck size={16} /> {verifying ? 'Verifying chain…' : 'Verify chain integrity'}
        </button>
      </div>

      <DisclosureBanner>
        Each entry stores a SHA-256 hash of its own content plus the previous entry's hash. Verifying
        the chain recomputes every hash from scratch — if a single row were altered after the fact,
        verification would fail at that exact sequence number. Some entries also carry an optional{' '}
        <MapPin size={12} style={{ display: 'inline', verticalAlign: -1 }} /> device-location tag,
        captured with the actor's browser permission at the moment of the action (isolation
        verification, override sign-off, permit issuance) as corroborating evidence — it is
        client-reported and deliberately excluded from the cryptographic hash itself, so its
        absence never blocks or invalidates an action.
      </DisclosureBanner>

      {verifyResult && verifyResult !== 'error' && (
        <div className="card row gap-2" style={{ borderColor: verifyResult.valid ? 'rgba(21,127,60,0.3)' : 'rgba(179,38,30,0.3)', background: verifyResult.valid ? 'var(--status-clear-bg)' : 'var(--status-critical-bg)' }}>
          {verifyResult.valid ? <ShieldCheck size={20} color="var(--status-clear)" /> : <ShieldAlert size={20} color="var(--status-critical)" />}
          <span className="body-small" style={{ fontWeight: 600, color: verifyResult.valid ? 'var(--status-clear)' : 'var(--status-critical)' }}>
            {verifyResult.valid
              ? `Chain intact — ${verifyResult.entriesChecked} entries verified.`
              : `Chain broken at sequence #${verifyResult.firstBrokenSeq} — ${verifyResult.reason ?? 'hash mismatch'}.`}
          </span>
        </div>
      )}
      {verifyResult === 'error' && <ErrorBanner message="Could not verify the audit chain (platform administrator role required)." />}

      {error && <ErrorBanner message={error} />}

      {!entries ? (
        <Spinner label="Loading audit log…" />
      ) : entries.length === 0 ? (
        <EmptyState icon={<ScrollText size={32} />} title="No audit entries yet" description="Actions taken in this plant will appear here as they happen." />
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>#</th><th>When</th><th>Action</th><th>Entity</th><th>Actor role</th><th>Hash</th></tr></thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td data-label="#" className="mono">{e.seq}</td>
                  <td data-label="When">{formatDateTime(e.createdAt)}</td>
                  <td data-label="Action">
                    <span className="row gap-1" style={{ alignItems: 'center', display: 'inline-flex' }}>
                      {e.action.replace(/_/g, ' ')}
                      {e.geoLat != null && e.geoLng != null && (
                        <span
                          title={`Recorded near ${e.geoLat.toFixed(5)}, ${e.geoLng.toFixed(5)}${e.geoAccuracyM != null ? ` (±${Math.round(e.geoAccuracyM)} m)` : ''}`}
                          style={{ display: 'inline-flex' }}
                        >
                          <MapPin size={13} style={{ color: 'var(--accent)' }} />
                        </span>
                      )}
                    </span>
                  </td>
                  <td data-label="Entity"><span className="body-small text-tertiary">{e.entityType}</span></td>
                  <td data-label="Actor role">{e.actorRole ?? '—'}</td>
                  <td data-label="Hash"><span className="mono body-small" title={e.hash}>{e.hash.slice(0, 10)}…</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
